import _ from "lodash";
import { FC } from "react";
import { TColumn, TableColumn } from "../../utils/types";
import { Show } from "components/core";

interface TableBodyComponentProps<TData = any> {
  data: TData[];
  columns: TableColumn<TColumn<TData>>;
  renderItem?: FC<TData>;
  renderLoader?: FC;
  loading?: boolean;
  isEmpty?: boolean;
  showHeader: boolean;
  viewType?: "grid" | "list";
}

const TableBodyComponent: FC<TableBodyComponentProps> = ({
  data,
  renderItem,
  loading,
  renderLoader,
  columns,
  isEmpty,
  showHeader,
}) => {
  return (
    <div className="h-full overflow-auto">
      {/* Mobile (cards) - only when using column definitions (no custom renderItem) */}
      <div className="w-full px-4 md:hidden">
        <Show if={!renderItem && (columns?.length || 0) > 0}>
        <Show if={Boolean(loading)}>
          <div className="space-y-3 py-2">
            {[0, 1, 2].map((sk) => (
              <div
                key={sk}
                className="rounded-lg border border-background-500 bg-card-500 p-3 animate-pulse"
              >
                <div className="h-4 bg-gray-300 rounded w-1/2 mb-2" />
                <div className="h-3 bg-gray-200 rounded w-5/6 mb-1" />
                <div className="h-3 bg-gray-200 rounded w-2/3" />
              </div>
            ))}
          </div>
        </Show>

        <Show if={!loading}>
          <div className="space-y-3">
            {data?.map((item, index) => (
              <div
                key={index}
                className="rounded-lg border border-background-500 bg-card-500"
              >
                {columns?.map((col, idx) => (
                  <div
                    key={idx}
                    className="grid grid-cols-3 gap-2 px-3 py-2 border-b border-background-500 last:border-b-0"
                  >
                    <div className="col-span-1 text-[11px] uppercase text-header-100 truncate">
                      {col?.name}
                    </div>
                    <div className="col-span-2 text-sm text-header-400 break-words">
                      {col?.render
                        ? col.render(item, index) || "N/A"
                        : _.get(item, (col.accessor as string) ?? "N/A")}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </Show>
        </Show>
      </div>

      {/* Desktop and tablets (table) */}
      <div className={`w-full align-middle px-6 ${renderItem ? "block" : "hidden md:block"}`}>
        <table
          className="w-full table-fixed divide-y divide-background-500 border-x border-background-500"
          style={{ borderSpacing: 0 }}
        >
          <Show if={!isEmpty && showHeader}>
            <thead className="bg-gray-50">
              <tr>
                {columns?.map((columnItem, index) => (
                  <th
                    key={index}
                    scope="col"
                    className="sticky top-0 border-y border-background-500 bg-card-500 px-2 py-3 text-left text-xs font-light text-header-100 uppercase tracking-wider truncate"
                    style={{ width: columnItem.width || `${100 / columns.length}%` }}
                  >
                    <div className="truncate">{columnItem?.name}</div>
                  </th>
                ))}
              </tr>
            </thead>
          </Show>
          <tbody className="bg-card-500 divide-y divide-background-500">
            {loading
              ? renderLoader?.({}) ?? (
                  <tr>
                    {columns?.map((col, idx) => (
                      <td
                        key={idx}
                        className="px-6 py-4 text-sm text-header-400 border-b border-background-500"
                        style={{ width: col.width || `${100 / columns.length}%` }}
                      >
                        <div className="bg-gray-500 h-3 rounded-md w-full animate-pulse" />
                      </td>
                    ))}
                  </tr>
                )
              : data?.map(
                  (item, index) =>
                    renderItem?.(item) ?? (
                      <tr key={index}>
                        {columns?.map((col, idx) =>
                          col?.render ? (
                            <td
                              key={idx}
                              className="p-2 text-sm text-header-400 border-b border-background-500"
                              style={{ width: col.width || `${100 / columns.length}%` }}
                            >
                              <div className="break-words">
                                {col?.render(item, index) || "N/A"}
                              </div>
                            </td>
                          ) : (
                            <td
                              key={idx}
                              className="px-2 py-4 text-sm text-header-400 border-b border-background-500"
                              style={{ width: col.width || `${100 / columns.length}%` }}
                            >
                              <div className="break-words">
                                {_.get(item, (col.accessor as string) ?? "N/A")}
                              </div>
                            </td>
                          )
                        )}
                      </tr>
                    )
                )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TableBodyComponent;
